import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { CheckCircle, XCircle, Smartphone, Shield, Key, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/resources" className="dark-nav-link">RESOURCES</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-accordion-item ${isOpen ? 'active' : ''}`}>
      <button className="faq-accordion-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-accordion-question">{question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div className="faq-accordion-content">
          <p className="faq-answer">{answer}</p>
        </div>
      )}
    </div>
  );
};

const HardwareWalletRecoveryGuide = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Hardware Wallet Recovery (Ledger/Trezor): Seed Phrase Guide</title>
        <meta name="description" content="Lost access to Ledger or Trezor? Learn seed phrase recovery (not device PIN unlock). What we can recover, what we cannot. Non-custodial. Free assessment." />
        <meta name="keywords" content="Ledger recovery, Trezor recovery, hardware wallet seed, Ledger seed phrase, Trezor seed recovery, hardware wallet lost" />
        <link rel="canonical" href="https://arcana-crypto.com/hardware-wallet-seo.html" />
      </Helmet>

      <Navigation />
      
      <article className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container" style={{maxWidth: '900px'}}>
          
          {/* Header */}
          <header>
            <div style={{
              background: 'var(--nero-grigio)', 
              border: '1px solid var(--oro-arcana)', 
              padding: '24px',
              marginBottom: '40px'
            }}>
              <Smartphone size={48} color="var(--oro-arcana)" style={{display: 'block', margin: '0 auto 16px'}} />
              <h1 className="section-title" style={{fontSize: '42px', textAlign: 'left', marginBottom: '16px'}}>
                Hardware Wallet Recovery (Ledger/Trezor): Seed Phrase Recovery Guide
              </h1>
              <p className="section-intro" style={{fontSize: '18px', marginBottom: '16px'}}>
                Lost access to your <strong>Ledger</strong> or <strong>Trezor</strong> hardware wallet? 
                We specialize in <strong>seed phrase recovery</strong> (missing words, typos, wrong order) - 
                NOT device PIN unlock or firmware hacking.
              </p>
              <div style={{
                background: 'rgba(212, 175, 55, 0.1)', 
                border: '1px solid var(--oro-arcana)',
                padding: '16px',
                marginTop: '20px'
              }}>
                <p style={{color: 'var(--oro-arcana)', fontWeight: '600', marginBottom: '8px'}}>
                  ⚠️ IMPORTANT CLARIFICATION:
                </p>
                <p style={{color: 'var(--grigio-chiaro)', fontSize: '15px'}}>
                  We recover <strong>seed phrases</strong> (24-word BIP39 recovery phrase). 
                  We do <strong>NOT</strong> unlock devices, crack PINs, or bypass hardware security. 
                  Seed phrase recovery works <strong>independently</strong> of the physical device.
                </p>
              </div>
            </div>
          </header>

          {/* Key Takeaways */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Key Takeaways
            </h2>
            <ul className="column-list">
              <li><strong>Seed phrase recovery</strong> = YES (missing words, typos, order). Device PIN unlock = NO.</li>
              <li>Ledger/Trezor use standard <strong>BIP39</strong> (24 words typically).</li>
              <li>Recovery works on <strong>any wallet software</strong> - device not needed.</li>
              <li>Passphrase (25th word) complicates recovery if forgotten.</li>
              <li>Non-custodial recovery possible with proper methodology.</li>
            </ul>
          </section>

          {/* What We CAN Recover */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <CheckCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What We CAN Recover
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li><strong>Missing seed words</strong> (1-5 words from 24-word Ledger/Trezor seed)</li>
                <li><strong>Seed word typos</strong> (1-2 words with spelling errors)</li>
                <li><strong>Wrong order</strong> (12 or 24 words in incorrect sequence)</li>
                <li><strong>Passphrase issues</strong> (forgotten 25th word / extension word)</li>
                <li><strong>Derivation path issues</strong> (funds not showing after restore)</li>
              </ul>
              <p style={{marginTop: '20px', color: 'var(--oro-arcana)', fontWeight: '600'}}>
                → All recoverable using standard BIP39 recovery methods
              </p>
            </div>
          </section>

          {/* What We CANNOT Do */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <XCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What We CANNOT Do
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Unlock device <strong>PIN</strong> (hardware security is unbreakable)</li>
                <li>Bypass Ledger/Trezor <strong>firmware security</strong></li>
                <li>Extract seed from <strong>locked device</strong></li>
                <li>Recover from <strong>destroyed/corrupted device</strong> without seed</li>
              </ul>
              <p style={{marginTop: '20px', color: 'var(--grigio-medio)', fontStyle: 'italic', fontSize: '14px'}}>
                → If device is locked and you have NO seed backup, funds are not recoverable by anyone (this is the security design).
              </p>
            </div>
          </section>

          {/* Technical Explanation */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Key size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Technical Explanation (Simplified)
            </h2>
            <div className="legal-content">
              <p>
                <strong>Hardware wallets</strong> (Ledger, Trezor) generate a <strong>24-word BIP39 seed phrase</strong> 
                during setup. This seed is the <strong>master backup</strong> - it can restore your wallet on 
                ANY compatible software (Electrum, Exodus, etc.), not just the original device.
              </p>
              <p style={{marginTop: '16px'}}>
                The device PIN protects the <strong>device</strong>, not the seed. If you have the seed (even with 
                missing words or typos), recovery is possible using standard <strong>BIP39 recovery techniques</strong> 
                independent of the hardware device.
              </p>
              <p style={{marginTop: '16px'}}>
                Common scenarios:
              </p>
              <ul style={{marginTop: '12px', marginLeft: '20px'}}>
                <li><strong>Scenario 1:</strong> Lost device, have seed backup with 1-2 missing words → Recoverable</li>
                <li><strong>Scenario 2:</strong> Device works, forgot PIN, have seed backup → Use seed to restore (no recovery needed)</li>
                <li><strong>Scenario 3:</strong> Device lost, NO seed backup → Not recoverable</li>
                <li><strong>Scenario 4:</strong> Have seed but wallet shows zero (derivation path issue) → Recoverable</li>
              </ul>
            </div>
          </section>

          {/* Non-Custodial & Privacy */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Shield size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Non-Custodial & Privacy
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>We recover <strong>seed phrases</strong>, not device access</li>
                <li>Strictly non-custodial: no fund access or control</li>
                <li>Works with seed backup (independent of device)</li>
                <li>Encrypted communications and offline processing</li>
                <li>Complete seed delivered securely - you restore on any wallet</li>
              </ul>
            </div>
          </section>

          {/* FAQ Hardware-Specific */}
          <section style={{marginBottom: '80px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '32px'}}>
              Hardware Wallet FAQ
            </h2>
            <div className="faq-accordion">
              <FAQItem 
                question="Can you unlock my Ledger/Trezor PIN?"
                answer="No. Hardware wallet PINs are designed to be unrecoverable (security feature). We can only help with seed phrase recovery."
              />
              <FAQItem 
                question="My device is broken - can you extract the seed?"
                answer="No. Seeds are encrypted inside secure elements and cannot be extracted. You need your original seed backup."
              />
              <FAQItem 
                question="I have the seed - why do I need recovery?"
                answer="If your seed has missing words, typos, or wrong order, standard restoration won't work. That's where we help."
              />
              <FAQItem 
                question="Can I restore Ledger seed on Trezor (or vice versa)?"
                answer="Yes! BIP39 seeds are cross-compatible. A Ledger seed works on Trezor, Electrum, Exodus, or any BIP39 wallet."
              />
              <FAQItem 
                question="What about Ledger Nano S vs X vs S Plus?"
                answer="All Ledger models use the same BIP39 standard. Seed recovery works the same regardless of device model."
              />
              <FAQItem 
                question="Do you support other hardware wallets?"
                answer="Yes - any BIP39-compliant hardware wallet (KeepKey, SafePal, BitBox, ColdCard). All use standard seed phrases."
              />
              <FAQItem 
                question="How much does hardware wallet seed recovery cost?"
                answer="Same as regular seed recovery: £300-£3,000 depending on missing words (1-5). Device brand doesn't affect price."
              />
            </div>
          </section>

          {/* CTA */}
          <section className="cta-section" style={{marginTop: '80px', padding: '60px 48px'}}>
            <h2 className="cta-title" style={{fontSize: '36px'}}>
              Hardware Wallet Seed Issues?
            </h2>
            <p className="cta-subtitle" style={{marginBottom: '40px'}}>
              Free assessment for Ledger, Trezor, and all BIP39 hardware wallets
            </p>
            <button className="btn-primary" style={{fontSize: '18px', padding: '18px 40px'}} onClick={() => window.location.href = '/#assessment'}>
              FREE HARDWARE WALLET ASSESSMENT
            </button>
            <p className="hero-small-text" style={{marginTop: '24px'}}>
              Ledger • Trezor • KeepKey • SafePal • All BIP39 devices
            </p>
            <div style={{marginTop: '32px'}}>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '12px'}}>
                <strong>Related Services:</strong>
              </p>
              <a href="/seed-phrase-recovery" className="card-link" style={{fontSize: '16px', marginRight: '20px'}}>
                Seed Phrase Recovery →
              </a>
              <a href="/resources/passphrase-25th-word-recovery" className="card-link" style={{fontSize: '16px'}}>
                Passphrase Recovery →
              </a>
            </div>
          </section>

          {/* Breadcrumb Back */}
          <div style={{marginTop: '60px', textAlign: 'center'}}>
            <a href="/resources" className="card-link" style={{fontSize: '16px'}}>
              ← Back to All Resources
            </a>
          </div>

        </div>
      </article>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HardwareWalletRecoveryGuide;
